/**
 * @fileoverview Visualization overlay components
 * 
 * Contains overlay components for:
 * - RadiatorRatingOverlay: Shows radiator output ratings
 * - TemperatureRatingOverlay: Shows room temperature rankings
 * - HeatFluxOverlay: Visualizes heat flow between rooms
 * - UValueRatingOverlay: Shows thermal resistance ratings
 * 
 * @module HydraulicBalancing/components/OverlayComponents
 * @author HeatCraftHP Team
 * @version 2.0.0
 */

import React, { useMemo } from 'react';

import {
  SVG_DIMENSIONS,
  COLORS,
} from '../constants';

import {
  getNeighborTemperatures,
  formatNumber,
} from '../utils';

const { ROOM_WIDTH, ROOM_HEIGHT, MARGIN_LEFT, GROUND_Y } = SVG_DIMENSIONS;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Gets room center position for overlay elements
 * @param {number} floorIndex - Floor number
 * @param {number} colIndex - Column index
 * @returns {Object} { x, y } center coordinates
 */
const getRoomCenter = (floorIndex, colIndex) => ({
  x: MARGIN_LEFT + ROOM_WIDTH * colIndex + ROOM_WIDTH / 2,
  y: GROUND_Y - ROOM_HEIGHT * (floorIndex + 1) + ROOM_HEIGHT / 2,
});

// ============================================================================
// HEAT ARROW PATH GENERATOR
// ============================================================================

/**
 * Generates SVG path for heat arrow indicator (scaled for smaller rooms)
 * @param {number} startX - Arrow base X position
 * @param {number} startY - Arrow base Y position
 * @param {number} rating - Heat output rating (0-1 scale, 1 = 5kW)
 * @returns {string} SVG path string
 */
const generateRadiatorHeatArrow = (startX, startY, rating) => {
  const arrowHeight = Math.max(5, rating * 25); // Scale arrow height (max ~25px)
  
  return `M${startX} ${startY} 
          L${startX + 3} ${startY} 
          L${startX + 3} ${startY - arrowHeight} 
          L${startX + 6} ${startY - arrowHeight} 
          L${startX} ${startY - arrowHeight - 6} 
          L${startX - 6} ${startY - arrowHeight} 
          L${startX - 3} ${startY - arrowHeight} 
          L${startX - 3} ${startY}z`;
};

// ============================================================================
// RADIATOR RATING OVERLAY
// ============================================================================

/**
 * Displays radiator output ratings for each room
 * Shows heat output with arrow indicator and supply/return temperatures
 * Styled to match HeatPumpSimulation's RadiatorRatingOverlay
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array<Array<Object>>} props.rooms - Building grid
 * @param {number} props.tOutdoor - Outdoor temperature for design load calculation
 * @param {number} props.tSupply - Supply water temperature
 */
export const RadiatorRatingOverlay = React.memo(({ rooms, tOutdoor, tSupply = 55 }) => {
  // Calculate ratings for all rooms
  const ratings = useMemo(() => {
    const result = [];
    
    rooms.forEach((floor, floorIndex) => {
      floor.forEach((room, colIndex) => {
        if (!room) return;
        
        const qrad = room.qrad[room.qrad.length - 1] || 0;
        const tReturn = room.Tout || tSupply;
        const nRad = room.nRad || 8;
        
        // Rating scale: 0-1 where 1 = 5kW
        const rating = Math.min(qrad / 5000, 1);
        
        const { x, y } = getRoomCenter(floorIndex, colIndex);
        
        result.push({
          key: `rad-${floorIndex}-${colIndex}`,
          x,
          y,
          qrad,
          rating,
          tSupply,
          tReturn,
          nRad,
        });
      });
    });
    
    return result;
  }, [rooms, tSupply]);

  return (
    <g className="radiator-rating-overlay">
      {ratings.map(({ key, x, y, qrad, rating, tSupply: supplyTemp, tReturn, nRad }) => {
        // Position calculations (scaled for smaller room)
        const textX = x - ROOM_WIDTH * 0.45;
        const textY = y - ROOM_HEIGHT * 0.4;

        const boxX = x - ROOM_WIDTH * 0.36;

        const arrowBaseX = x - ROOM_WIDTH * 0.35 + (14.5 + 7 * nRad) * 0.5;
        const arrowBaseY = y + ROOM_HEIGHT * 0.1;
        
        return (
          <g key={key}>
            {/* Semi-transparent background over room */}
            <rect
              x={x - ROOM_WIDTH / 2 + 1}
              y={y - ROOM_HEIGHT / 2 + 1}
              width={ROOM_WIDTH - 2}
              height={ROOM_HEIGHT - 2}
              fill="rgba(255, 255, 255, 0.75)"
            />
            
            {/* Heat output label */}
            <rect
              x={textX - 8}
              y={textY - 14}
              width={100}
              height={40}
              fill="white"
            />
            <text
              x={textX}
              y={textY}
              fill={rating > 0 ? "#52c41a" : "#999"}
              fontSize={12}
              fontWeight="bold"
            >
              {(qrad / 1000).toFixed(2)} kW
            </text>
            
            {/* Heat arrow indicator */}
            <path
              fill={rating > 0 ? "#52c41a" : "#ccc"}
              d={generateRadiatorHeatArrow(arrowBaseX, arrowBaseY, rating)}
            />
            
            {/* Temperature display box */}
            <rect
              x={boxX}
              y={arrowBaseY + 1}
              width={14.5 + 7 * nRad}
              height={40}
              fill="rgba(255, 255, 255, 0.95)"
              stroke="rgba(0, 0, 0, 0.4)"
              strokeWidth={1}
              rx={2}
            />
            
            {/* Supply temperature */}
            <text
              x={boxX + 8}
              y={arrowBaseY + 16}
              fill="#ff4d4f"
              fontSize={12}
            >
              {supplyTemp.toFixed(0)}°C
            </text>
            
            {/* Return temperature */}
            <text
              x={boxX + 8}
              y={arrowBaseY + 34}
              fill="#1890ff"
              fontSize={12}
            >
              {tReturn.toFixed(1)}°C
            </text>
          </g>
        );
      })}
    </g>
  );
});

RadiatorRatingOverlay.displayName = 'RadiatorRatingOverlay';

// ============================================================================
// TEMPERATURE RATING OVERLAY
// ============================================================================

/**
 * Displays temperature ratings for each room
 * Shows current temperature vs setpoint with ranking
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array<Array<Object>>} props.rooms - Building grid
 */
export const TemperatureRatingOverlay = React.memo(({ rooms }) => {
  const ratings = useMemo(() => {
    const result = [];
    
    rooms.forEach((floor, floorIndex) => {
      floor.forEach((room, colIndex) => {
        if (!room) return;
        
        const tRoom = room.Tc[room.Tc.length - 1];
        const tSetpoint = room.Treq;
        const deviation = tRoom - tSetpoint;
        
        const { x: centerX, y: centerY } = getRoomCenter(floorIndex, colIndex);
        const { x: topLeftX, y: topLeftY } = getRoomTopLeft(floorIndex, colIndex);
        
        // Color based on deviation from setpoint
        let edgeColor = COLORS.SECONDARY; // Blue color for ranking
        
        result.push({
          key: `temp-${floorIndex}-${colIndex}`,
          centerX,
          centerY,
          topLeftX,
          topLeftY,
          tRoom,
          tSetpoint,
          deviation,
          edgeColor,
        });
      });
    });
    
    // Sort by temperature (rounded to 1 decimal to prevent flickering)
    result.sort((a, b) => {
      const aRounded = Math.round(a.tRoom * 10) / 10;
      const bRounded = Math.round(b.tRoom * 10) / 10;
      return bRounded - aRounded;
    });
    result.forEach((item, index) => {
      item.rank = index + 1;
    });
    
    return result;
  }, [rooms]);

  return (
    <g className="temperature-rating-overlay">
      {ratings.map(({ key, centerX, centerY, topLeftX, topLeftY, tRoom, rank, edgeColor }) => (
        <g key={key}>
          {/* Semi-transparent background covering room */}
          <rect
            x={topLeftX + 1}
            y={topLeftY + 1}
            width={ROOM_WIDTH - 2}
            height={ROOM_HEIGHT - 2}
            fill="white"
            opacity={0.85}
          />
          
          {/* Temperature in center - red color */}
          <text
            x={centerX}
            y={centerY + 3}
            textAnchor="middle"
            fill="#d32f2f"
            fontSize={13}
            fontWeight="bold"
          >
            {formatNumber(tRoom, 1)}°C
          </text>
          
          {/* Ranking in top-left corner - blue color */}
          <text
            x={topLeftX + 10}
            y={topLeftY + 15}
            textAnchor="start"
            fill={edgeColor}
            fontSize={12}
            fontWeight="600"
          >
            #{rank}
          </text>
        </g>
      ))}
    </g>
  );
});

TemperatureRatingOverlay.displayName = 'TemperatureRatingOverlay';

// ============================================================================
// HEAT FLUX ARROW PATH GENERATOR
// ============================================================================

// Blue color for heat flux arrows
const HEAT_FLUX_COLOR = '#1890ff';

/**
 * Generates SVG path for heat flow arrow
 * Arrow base starts at the given position and extends outward
 * @param {string} direction - Arrow direction: 'west', 'east', 'north', 'south'
 * @param {Object} position - {x, y} position of the arrow BASE (at room edge)
 * @param {number} magnitude - Heat flux magnitude (temperature difference)
 * @returns {string} SVG path string
 */
const generateHeatArrow = (direction, position, magnitude) => {
  const { x, y } = position;
  // Scale arrow length: min 10px, max 35px, proportional to magnitude
  const arrowLength = Math.min(Math.max(magnitude * 1.8, 5), 35);
  const halfWidth = 3;
  const headWidth = 6;
  const headLength = 8;

  // Arrow base is at (x, y), arrow extends OUTWARD from that point
  switch (direction) {
    case 'west':
      // Base at x, extends left (negative x)
      return `M${x} ${y - halfWidth} L${x} ${y + halfWidth} L${x - arrowLength} ${y + halfWidth} 
              L${x - arrowLength} ${y + headWidth} L${x - arrowLength - headLength} ${y} 
              L${x - arrowLength} ${y - headWidth} L${x - arrowLength} ${y - halfWidth} Z`;

    case 'east':
      // Base at x, extends right (positive x)
      return `M${x} ${y - halfWidth} L${x} ${y + halfWidth} L${x + arrowLength} ${y + halfWidth} 
              L${x + arrowLength} ${y + headWidth} L${x + arrowLength + headLength} ${y} 
              L${x + arrowLength} ${y - headWidth} L${x + arrowLength} ${y - halfWidth} Z`;

    case 'north':
      // Base at y, extends up (negative y in SVG)
      return `M${x - halfWidth} ${y} L${x + halfWidth} ${y} L${x + halfWidth} ${y - arrowLength} 
              L${x + headWidth} ${y - arrowLength} L${x} ${y - arrowLength - headLength} 
              L${x - headWidth} ${y - arrowLength} L${x - halfWidth} ${y - arrowLength} Z`;

    case 'south':
      // Base at y, extends down (positive y in SVG)
      return `M${x - halfWidth} ${y} L${x + halfWidth} ${y} L${x + halfWidth} ${y + arrowLength} 
              L${x + headWidth} ${y + arrowLength} L${x} ${y + arrowLength + headLength} 
              L${x - headWidth} ${y + arrowLength} L${x - halfWidth} ${y + arrowLength} Z`;

    default:
      return '';
  }
};

/**
 * Gets room top-left position for overlay rectangles
 * @param {number} floorIndex - Floor number
 * @param {number} colIndex - Column index
 * @returns {Object} { x, y } top-left coordinates
 */
const getRoomTopLeft = (floorIndex, colIndex) => ({
  x: MARGIN_LEFT + ROOM_WIDTH * colIndex,
  y: GROUND_Y - ROOM_HEIGHT * (floorIndex + 1),
});

// ============================================================================
// HEAT FLUX OVERLAY
// ============================================================================

/**
 * Visualizes heat flow at each room edge
 * 
 * Rules:
 * 1. Each edge has exactly ONE arrow
 * 2. For exterior edges: arrow points outward if Troom > Toutdoor (heat loss),
 *    inward if Troom < Toutdoor (heat gain)
 * 3. For interior edges (room-to-room): arrow points from warmer to cooler room
 * 4. Interior edges are only processed once (from room with lower indices)
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array<Array<Object>>} props.rooms - Building grid
 * @param {number} props.tOutdoor - Outdoor temperature
 */
export const HeatFluxOverlay = React.memo(({ rooms, tOutdoor }) => {
  // Collect room positions for overlay rects
  const roomRects = useMemo(() => {
    const rects = [];
    rooms.forEach((floor, floorIndex) => {
      floor.forEach((room, colIndex) => {
        if (!room) return;
        const { x, y } = getRoomTopLeft(floorIndex, colIndex);
        const { x: centerX, y: centerY } = getRoomCenter(floorIndex, colIndex);
        const tRoom = room.Tc[room.Tc.length - 1];
        const qRad = room.qrad[room.qrad.length - 1] || 0;

        // Use pre-calculated losses from room state (already calculated in hooks.js)
        const envelopeLoss = room.envelopeLoss || 0;
        const ventLoss = room.ventilationLoss || 0;
        const netHeat = qRad - envelopeLoss - ventLoss;
        
        rects.push({
          key: `room-overlay-${floorIndex}-${colIndex}`,
          x,
          y,
          centerX,
          centerY,
          temperature: tRoom,
          qRad,
          envelopeLoss,
          ventLoss,
          netHeat,
        });
      });
    });
    return rects;
  }, [rooms]);

  const arrows = useMemo(() => {
    const result = [];
    
    rooms.forEach((floor, floorIndex) => {
      floor.forEach((room, colIndex) => {
        if (!room) return;
        
        const tRoom = room.Tc[room.Tc.length - 1];
        const { x: centerX, y: centerY } = getRoomCenter(floorIndex, colIndex);
        const neighbors = getNeighborTemperatures(rooms, floorIndex, colIndex);
                
        // ===== EAST EDGE =====
        const eastRoom = rooms[floorIndex]?.[colIndex + 1];
        if (eastRoom) {
          // Interior edge - only process from the LEFT room (this one)
          const tEast = neighbors.east;
          const eastDT = tRoom - tEast;
          if (Math.abs(eastDT) > 0) {
            result.push({
              key: `edge-${floorIndex}-${colIndex}-E`,
              x: centerX + ROOM_WIDTH / 2,
              y: centerY,
              direction: eastDT > 0 ? 'east' : 'west', // flows from warm to cold
              magnitude: Math.abs(eastDT),
            });
          }
        } else {
          // Exterior edge
          const eastDT = tRoom - tOutdoor;
          if (Math.abs(eastDT) > 0) {
            result.push({
              key: `edge-${floorIndex}-${colIndex}-E`,
              x: centerX + ROOM_WIDTH / 2,
              y: centerY,
              direction: eastDT > 0 ? 'east' : 'west',
              magnitude: Math.abs(eastDT),
            });
          }
        }
        
        // ===== WEST EDGE (only if NO room to the west - exterior only) =====
        const westRoom = rooms[floorIndex]?.[colIndex - 1];
        if (!westRoom) {
          // Exterior edge only (interior handled by west room's east edge)
          const westDT = tRoom - tOutdoor;
          if (Math.abs(westDT) > 0) {
            result.push({
              key: `edge-${floorIndex}-${colIndex}-W`,
              x: centerX - ROOM_WIDTH / 2,
              y: centerY,
              direction: westDT > 0 ? 'west' : 'east',
              magnitude: Math.abs(westDT),
            });
          }
        }
        
        // ===== TOP EDGE (ceiling/roof) =====
        const aboveRoom = rooms[floorIndex + 1]?.[colIndex];
        if (aboveRoom) {
          // Interior edge - only process from the LOWER room (this one)
          const tAbove = neighbors.above;
          const topDT = tRoom - tAbove;
          if (Math.abs(topDT) > 0) {
            result.push({
              key: `edge-${floorIndex}-${colIndex}-T`,
              x: centerX + 20, // offset to not overlap with north arrow
              y: centerY - ROOM_HEIGHT / 2,
              direction: topDT > 0 ? 'north' : 'south', // north = up in SVG
              magnitude: Math.abs(topDT),
            });
          }
        } else {
          // Exterior edge (roof)
          const topDT = tRoom - tOutdoor;
          if (Math.abs(topDT) > 0) {
            result.push({
              key: `edge-${floorIndex}-${colIndex}-T`,
              x: centerX + 20,
              y: centerY - ROOM_HEIGHT / 2,
              direction: topDT > 0 ? 'north' : 'south',
              magnitude: Math.abs(topDT),
            });
          }
        }
        
        // ===== BOTTOM EDGE (floor) - only if NO room below AND not ground floor =====
        const belowRoom = rooms[floorIndex - 1]?.[colIndex];
        if (!belowRoom && floorIndex > 0) {
          // Exterior edge only (interior handled by below room's top edge)
          const groundTemp = Math.max(tOutdoor, 8);
          const bottomDT = tRoom - groundTemp;
          if (Math.abs(bottomDT) > 0) {
            result.push({
              key: `edge-${floorIndex}-${colIndex}-B`,
              x: centerX + 20,
              y: centerY + ROOM_HEIGHT / 2,
              direction: bottomDT > 0 ? 'south' : 'north',
              magnitude: Math.abs(bottomDT),
            });
          }
        }
      });
    });
    
    return result;
  }, [rooms, tOutdoor]);

  return (
    <g className="heat-flux-overlay">
      {/* Semi-transparent overlay on each room */}
      {roomRects.map(({ key, x, y, centerX, centerY, temperature, qRad, envelopeLoss, ventLoss, netHeat }) => {
        const qRadKW = (qRad / 1000).toFixed(2);
        const envLossKW = (envelopeLoss / 1000).toFixed(2);
        const ventLossKW = (ventLoss / 1000).toFixed(2);
        const netHeatKW = (netHeat / 1000).toFixed(2);
        
        return (
          <g key={key}>
            <rect
              x={x + 1}
              y={y + 1}
              width={ROOM_WIDTH - 2}
              height={ROOM_HEIGHT - 2}
              fill="white"
              opacity={0.85}
            />
            
            {/* Room temperature - top left */}
            <text
              x={x + 8}
              y={y + 15}
              textAnchor="start"
              fill="#d32f2f"
              fontSize={12}
            >
              {temperature.toFixed(1)}°C
            </text>
            
            {/* Heat balance equation - centered */}
            <text
              x={centerX}
              y={centerY - 8}
              textAnchor="middle"
              fontSize={11}
              fontWeight="600"
            >
              <tspan fill="#2e7d32">+{qRadKW}</tspan>
              <tspan fill="#666"> − </tspan>
              <tspan fill="#d32f2f">{envLossKW}</tspan>
              <tspan fill="#666"> − </tspan>
              <tspan fill="#c62828">{ventLossKW}</tspan>
              <tspan fill="#666"> = </tspan>
              <tspan fill={netHeat >= 0 ? "#2e7d32" : "#c62828"}>{netHeat >= 0 ? '+' : ''}{netHeatKW}</tspan>
              <tspan fill="#666" dx="4"> kW</tspan>

            </text>
            
            {/* Labels - centered below equation */}
            <text
              x={centerX}
              y={centerY + 8}
              textAnchor="middle"
              fontSize={10}
              fill="#555"
              fontWeight="600"
            >
              <tspan>Q</tspan>
              <tspan fontSize={8} baselineShift="sub">rad</tspan>
              <tspan> − Q</tspan>
              <tspan fontSize={8} baselineShift="sub">env</tspan>
              <tspan> − Q</tspan>
              <tspan fontSize={8} baselineShift="sub">vent</tspan>
              <tspan> = Net</tspan>
            </text>
          </g>
        );
      })}
      
      {/* Heat flux arrows */}
      {arrows.map(({ key, x, y, direction, magnitude }) => 
        magnitude > 0 ? (
          <path
            key={key}
            fill={HEAT_FLUX_COLOR}
            opacity={0.85}
            d={generateHeatArrow(direction, { x, y }, magnitude)}
          />
        ) : null
      )}
    </g>
  );
});

HeatFluxOverlay.displayName = 'HeatFluxOverlay';

// ============================================================================
// U-VALUE RATING OVERLAY
// ============================================================================

/**
 * Displays U-value ratings for each room
 * Shows thermal transmittance (lower is better insulation)
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array<Array<Object>>} props.rooms - Building grid
 */
export const UValueRatingOverlay = React.memo(({ rooms }) => {
  const ratings = useMemo(() => {
    const result = [];
    
    rooms.forEach((floor, floorIndex) => {
      floor.forEach((room, colIndex) => {
        if (!room) return;
        
        const uValue = room.Uout;
        const { x: centerX, y: centerY } = getRoomCenter(floorIndex, colIndex);
        const { x: topLeftX, y: topLeftY } = getRoomTopLeft(floorIndex, colIndex);
        
        result.push({
          key: `uval-${floorIndex}-${colIndex}`,
          centerX,
          centerY,
          topLeftX,
          topLeftY,
          uValue,
        });
      });
    });
    
    // Sort by U-value (high to low)
    result.sort((a, b) => b.uValue - a.uValue);
    result.forEach((item, index) => {
      item.rank = index + 1;
    });
    
    return result;
  }, [rooms]);

  return (
    <g className="uvalue-rating-overlay">
      {ratings.map(({ key, centerX, centerY, topLeftX, topLeftY, uValue, rank }) => (
        <g key={key}>
          {/* Semi-transparent background covering room */}
          <rect
            x={topLeftX + 1}
            y={topLeftY + 1}
            width={ROOM_WIDTH - 2}
            height={ROOM_HEIGHT - 2}
            fill="white"
            opacity={0.85}
          />
          
          {/* U-value label */}
          <text
            x={centerX}
            y={centerY - 8}
            textAnchor="middle"
            fill="#666"
            fontSize={10}
          >
            U-value
          </text>
          
          {/* U-value in center - blue color */}
          <text
            x={centerX}
            y={centerY + 6}
            textAnchor="middle"
            fill={COLORS.SECONDARY}
            fontSize={13}
            fontWeight="bold"
          >
            {formatNumber(uValue, 2)} W/m²K
          </text>
          
          {/* Ranking in top-left corner - blue color */}
          <text
            x={topLeftX + 10}
            y={topLeftY + 15}
            textAnchor="start"
            fill={COLORS.SECONDARY}
            fontSize={12}
            fontWeight="600"
          >
            #{rank}
          </text>
        </g>
      ))}
    </g>
  );
});

UValueRatingOverlay.displayName = 'UValueRatingOverlay';

// Named exports for individual components
const OverlayComponents = {
  RadiatorRatingOverlay,
  TemperatureRatingOverlay,
  HeatFluxOverlay,
  UValueRatingOverlay,
};

export default OverlayComponents;
